import Row from '@app/components/layout/Row'
import Column from '@app/components/layout/Column'
import { faShare } from '@fortawesome/pro-regular-svg-icons'
import {
  faLocationDot,
  faCalendar,
  faClock,
  faCalendarArrowDown,
  faCalendarPlus,
} from '@fortawesome/pro-light-svg-icons'
import { useLanguage, useTranslations } from '@i18n/useLanguage'
import { Card } from '@app/components/blocks/card/Card'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { AddToCalendarModal } from '@app/components/blocks/modal/AddToCalendarModal'
import { useMemo, useState } from 'react'
import { IconList } from '@app/components/blocks/iconList/IconList'
import { Button } from '@app/components/core/button/Button'
import { ViewEventModel } from 'functions/app/event/view'
import { Style } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'
import { Title } from '@app/components/core/typography/Title'
import { useAuthState, useClient } from '@based/sdk/react'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { EVENT_USER_ROLES } from '@schema/eventRoles'
import { Text } from '@app/components/core/typography/Text'

export const DataCard = ({
  event,
  style,
}: {
  event: ViewEventModel
  style?: Style
}) => {
  const [language, _] = useLanguage()
  const t = useTranslations().app
  const [shareEventModal, setShareEventModal] = useState(false)
  const [pending, setPending] = useState(false)
  const [calendarModal, setCalendarModal] = useState(false)
  const startDate = useMemo(() => new Date(event.start), [event.start])
  const endDate = useMemo(() => new Date(event.end), [event.end])
  const isMobile = useMediaQuery()
  const authState = useAuthState()
  const client = useClient()
  const { withAuthCheck } = useAuthModal()

  const currentUserRole = event.users?.find(
    (u) => u.id === Number(authState.userId ?? ''),
  )?.$role
  const isOwner = currentUserRole === EVENT_USER_ROLES.owner
  const hasJoined = !!currentUserRole && !isOwner

  const handleJoinLeave = () => {
    void withAuthCheck(async () => {
      setPending(true)

      try {
        if (hasJoined) {
          await client.call('leave-event', { eventId: event.id })
        } else {
          await client.call('join-event', { eventId: event.id })
        }
      } finally {
        setPending(false)
      }
    })
  }

  const buttonLabel = hasJoined ? t.eventPage.leaveEvent : t.eventPage.joinEvent
  const isOnline = !!event.meetingLink

  const isPast = useMemo(() => {
    const now = Date.now()
    if (event.end) {
      return event.end < now
    }
    const startOfToday = new Date(now)
    startOfToday.setHours(0, 0, 0, 0)
    return event.start < startOfToday.getTime()
  }, [event.start, event.end])

  const showAttendButton = hasJoined && isOnline && !isPast

  const iconListItems = useMemo(() => {
    const items = []
    if (event.location?.address) {
      items.push({
        icon: faLocationDot,
        text: event.location.address,
      })
    }

    if (event.start && !event.end) {
      items.push(
        {
          icon: faCalendar,
          text: startDate.toLocaleString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        },
        {
          icon: faClock,
          text: startDate.toLocaleString(language, {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
      )
    } else if (
      event.start &&
      event.end &&
      startDate.toLocaleString(language) === endDate.toLocaleString(language)
    ) {
      items.push(
        {
          icon: faCalendar,
          text: startDate.toLocaleString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        },
        {
          icon: faClock,
          text: `${startDate.toLocaleString(language, {
            hour: '2-digit',
            minute: '2-digit',
          })} - ${endDate.toLocaleString(language, {
            hour: '2-digit',
            minute: '2-digit',
          })}`,
        },
      )
    } else if (event.start && event.end) {
      items.push(
        {
          icon: faCalendarArrowDown,
          text: startDate.toLocaleString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
        {
          icon: faCalendar,
          text: `${endDate.toLocaleString(language, {
            hour: '2-digit',
            minute: '2-digit',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}`,
        },
      )
    }

    return items
  }, [event.location, startDate, endDate, language])

  return (
    <>
      <Card
        style={{
          padding: 0,
          ...style,
        }}
        contentStyle={{
          padding: '1.5rem',
        }}
      >
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ height: '100%', justifyContent: 'space-between' }}
        >
          {isMobile && (
            <Title.Page styledAsLevel={3} style={{ margin: 0 }}>
              {event.title}
            </Title.Page>
          )}

          <IconList items={iconListItems} />
          {isPast && (
            <Text.Medium style={{ color: 'var(--gray-400)' }}>
              {t.eventPage.eventEnded}
            </Text.Medium>
          )}

          <Column gap="0.5rem" alignItems="stretch">
            {showAttendButton ? (
              <>
                <Button
                  label={t.eventPage.attendEvent}
                  onClick={() =>
                    window.open(event.meetingLink, '_blank', 'noopener')
                  }
                />
                <Button
                  label={t.eventPage.leaveEvent}
                  variant="secondary"
                  onClick={() => void handleJoinLeave()}
                  disabled={pending}
                />
              </>
            ) : (
              !isOwner && (
                <Button
                  label={buttonLabel}
                  onClick={() => void handleJoinLeave()}
                  disabled={pending}
                />
              )
            )}

            <Row gap="0.5rem" alignItems="stretch">
              <Button
                icon={faCalendarPlus}
                label={t.eventPage.addToCalendar}
                variant="secondary"
                onClick={() => setCalendarModal(true)}
                style={{ flexBasis: '50%' }}
              />
              <Button
                icon={faShare}
                label={t.eventPage.share}
                variant="secondary"
                onClick={() => setShareEventModal(true)}
                style={{ flexBasis: '50%' }}
              />
            </Row>
          </Column>
        </Column>
      </Card>

      <SocialShareModal
        isOpen={shareEventModal}
        onClose={() => setShareEventModal(false)}
        title={event.title}
      />

      <AddToCalendarModal
        isOpen={calendarModal}
        onClose={() => setCalendarModal(false)}
        event={event}
      />
    </>
  )
}
